import { Tag } from 'antd';
import { RcFile } from 'antd/es/upload/interface';

import { Input, InputPassword, TagInput } from '@atom/form/input/input';
import { Text } from '@atom/typography/typography';
import { FormSwitcher } from '@component/form-switcher/form-switcher';
import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { Form } from '@form/common.style';
import { FormError } from '@form/form-error';
import { isHris } from '@form/integration-base/utils';
import { Upload } from '@form/upload/upload';
import { BaseFormInterface } from '@interface/form/base-form.interface';

import { TextBlock, Wrapper } from './integration-base.style';

interface PemUploadProps {
  value?: string;
  onChange?: (value: string) => void;
}

const PemUpload = ({ value, onChange }: PemUploadProps) => {
  const handleFile = (file: RcFile): false => {
    const reader = new FileReader();
    reader.onload = (e) => onChange?.(e.target?.result as string);
    reader.readAsText(file);
    return false;
  };

  return (
    <>
      {value && <Tag color='success'>Private key configured</Tag>}
      <Upload
        accept='.pem'
        text={value ? 'Upload a new .pem file to replace the existing key' : 'Upload .pem private key file'}
        onFile={handleFile}
      />
    </>
  );
};

interface IntegrationBaseFormProps extends BaseFormInterface {
  serviceId: ConnectorServiceEnum;
}

export const IntegrationBaseForm = ({ form, fields, error, serviceId }: IntegrationBaseFormProps) => (
  <Form form={form} layout='vertical' name='integration'>
    {serviceId === ConnectorServiceEnum.Velory && (
      <Text>The user authenticating this connection must have the admin role in Velory.</Text>
    )}
    {isHris(serviceId) && (
      <Text>
        Note: API keys are personal and will provide data according to the user’s current permissions. To ensure
        uninterrupted access, Universal API recommends generating the API key using a dedicated non-employee user not
        tied to a specific employee.
      </Text>
    )}
    {fields?.map((field) => (
      <Wrapper key={field.label}>
        <Form.Item label={field.label} name={field.name} rules={[{ required: !field.optional }]}>
          {field.multiple ? (
            <TagInput placeholder={field.placeholder} />
          ) : field.type === 'password' ? (
            <InputPassword placeholder={field.placeholder} />
          ) : field.type === 'file' ? (
            <PemUpload />
          ) : (
            <Input placeholder={field.placeholder} />
          )}
        </Form.Item>
        {field.type === 'velory' && <TextBlock>.velory.com</TextBlock>}
        {field.type === 'jamf' && <TextBlock>.jamfcloud.com</TextBlock>}
        {field.type === 'sap' && <TextBlock>/odata/v2</TextBlock>}
      </Wrapper>
    ))}
    <Form.Item label='Enable' name='enabled' rules={[{ required: true }]}>
      <FormSwitcher form={form} name='enabled' />
    </Form.Item>
    <FormError error={error} errorField='' />
  </Form>
);
